import { useQuery } from "@tanstack/react-query";
import {
  RouteGetMerchflows_merchflowId_Details,
  type ResponseGetMerchflows_merchflowId_Details,
} from "@CommonApi/merchflows/_merchflowId_/details";
import {
  RouteGetMerchflows_merchflowId_StoresAll,
  type ResponseGetMerchflows_merchflowId_StoresAll,
} from "@CommonApi/merchflows/_merchflowId_/stores/all";
import {
  fetchCdtCategoryStores,
  fetchCdtCategoryTrees,
} from "src/pages/CDTTreeEditor/store/cdtTreeService";
import { fetchCdtRelease } from "src/pages/CdtStudio/store/cdtCompressionService";
import { API, getRequest } from "src/utils/axios";
import { Color, Text } from "src/elements";
import { useTranslation } from "src/i18n";

interface Props {
  merchflowId: number;
  generation?: { storeCodes: string[]; hasRelease: boolean };
}

const fetchFlowStores = async (merchflowId: number) => {
  const stores: string[] = [];
  for (let page = 1; ; page += 1) {
    const { data } = await getRequest<ResponseGetMerchflows_merchflowId_StoresAll>(
      `${API}${RouteGetMerchflows_merchflowId_StoresAll}`,
      { params: { merchflowId }, queryParams: { page, size: 100, viewMode: "list" } },
    );
    stores.push(...data.data.map((store) => store.store));
    if (page >= data.totalPages) return stores;
    if (data.data.length === 0) throw new Error("Incomplete flow stores response");
  }
};

export const fetchCdtSourceSummary = async ({ merchflowId, generation }: Props) => {
  const [flow, storeCodes, hasRelease] = await Promise.all([
    getRequest<ResponseGetMerchflows_merchflowId_Details>(
      `${API}${RouteGetMerchflows_merchflowId_Details}`,
      { params: { merchflowId } },
    ).then(({ data }) => data),
    generation ? generation.storeCodes : fetchFlowStores(merchflowId),
    generation
      ? generation.hasRelease
      : fetchCdtRelease(merchflowId).then((release) => release !== null),
  ]);
  if (flow.category_id == null) throw new Error("Flow category is required");
  const [assignments, trees] = await Promise.all([
    fetchCdtCategoryStores(flow.category_id),
    fetchCdtCategoryTrees(flow.category_id),
  ]);
  const byCode = new Map(assignments.map((store) => [store.storeCode, store]));
  const pins = new Map<number, { id: number; name: string | null; count: number }>();
  let onRelease = 0;
  let onPrimary = 0;
  for (const code of new Set(storeCodes)) {
    const store = byCode.get(code);
    if (!store) throw new Error("Incomplete category stores response");
    if (store.assignedTreeId !== null) {
      const pin = pins.get(store.assignedTreeId) ?? {
        id: store.assignedTreeId,
        name: store.assignedTreeName,
        count: 0,
      };
      pin.count += 1;
      pins.set(pin.id, pin);
    } else if (hasRelease) {
      onRelease += 1;
    } else {
      onPrimary += 1;
    }
  }
  const primary = trees.find((tree) => tree.isCategoryDefault);
  if (onPrimary > 0 && !primary) throw new Error("Primary CDT is required");
  return {
    pins: [...pins.values()].sort((a, b) => a.id - b.id),
    onRelease,
    onPrimary,
    primaryId: primary?.id,
    hasRelease,
  };
};

export const CdtSourceSummary = (props: Props) => {
  const { t } = useTranslation("common");
  const query = useQuery({
    queryKey: ["cdt-source-summary", props.merchflowId, props.generation],
    queryFn: () => fetchCdtSourceSummary(props),
    retry: false,
  });
  if (!query.isSuccess) return null;
  const { pins, onRelease, onPrimary, primaryId, hasRelease } = query.data;
  const parts = pins.length
    ? pins.map((pin) =>
        t(
          pin.count === 1
            ? "planogramRules.cdtSource.pinnedVersion_one"
            : "planogramRules.cdtSource.pinnedVersion_other",
          {
            count: pin.count,
            version: `#${pin.id}${pin.name ? ` ${pin.name}` : ""}`,
          },
        ),
      )
    : [t("planogramRules.cdtSource.pinnedStores_other", { count: 0 })];
  if (onRelease > 0 || hasRelease) {
    parts.push(
      t(
        onRelease === 1
          ? "planogramRules.cdtSource.onRelease_one"
          : "planogramRules.cdtSource.onRelease_other",
        { count: onRelease },
      ),
    );
  }
  if (onPrimary > 0 || !hasRelease) {
    parts.push(
      t(
        onPrimary === 1
          ? "planogramRules.cdtSource.onPrimary_one"
          : "planogramRules.cdtSource.onPrimary_other",
        {
          count: onPrimary,
          version: primaryId == null ? "" : ` #${primaryId}`,
        },
      ),
    );
  }
  return (
    <Text variant="small2" color={Color.textSecondary}>
      {parts.join(" · ")}
    </Text>
  );
};
